import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Users, Check, Loader2, Zap, AlertTriangle,
  TrendingUp, TrendingDown, Plus, Minus, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const TEST_TYPES = [
  { value: 'SJ', label: 'Squat Jump (SJ)' },
  { value: 'CMJ', label: 'Counter Movement Jump (CMJ)' },
  { value: 'CMJ_FREE_ARMS', label: 'CMJ Free Arms (a braccia libere)' },
  { value: 'CMJ_RIGHT_LEFT', label: 'CMJ Right / Left' },
  { value: 'LOADED_CMJ', label: 'Loaded CMJ' },
  { value: 'DROP_JUMP', label: 'Drop Jump' },
  { value: 'DRIFT_2D', label: 'Drift 2D (bilateral)' },
  { value: 'STIFFNESS', label: 'Stiffness' },
];

const TEST_LABELS = {
  SJ: 'Squat Jump', CMJ: 'Counter Movement Jump', CMJ_RIGHT_LEFT: 'CMJ Right/Left',
  LOADED_CMJ: 'Loaded CMJ', DROP_JUMP: 'Drop Jump',
  CMJ_FREE_ARMS: 'CMJ Free Arms', DRIFT_2D: 'Drift 2D', STIFFNESS: 'Stiffness',
};

export default function GroupJumpTestPage() {
  const { api } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (en, it) => language === 'IT' ? it : en;

  const [step, setStep] = useState('setup'); // setup | testing | results
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Setup state
  const [teamId, setTeamId] = useState('');
  const [testType, setTestType] = useState('');
  const [testDate, setTestDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [selectedAthletes, setSelectedAthletes] = useState([]);

  // Testing state
  const [sessionId, setSessionId] = useState(null);
  const [trialNum, setTrialNum] = useState(1);
  const [loadKg, setLoadKg] = useState(0);      // For LOADED_CMJ
  const [currentSide, setCurrentSide] = useState('RIGHT'); // For CMJ_RIGHT_LEFT
  const [entries, setEntries] = useState({});     // { athleteId: { height, contactTime, ...} }
  const [savedResults, setSavedResults] = useState([]);

  // Results state
  const [finalResult, setFinalResult] = useState(null);

  // Fetch teams
  useEffect(() => {
    api.get('/teams').then(r => setTeams(r.data)).catch(() => {});
  }, [api]);

  // Fetch athletes when team changes
  useEffect(() => {
    if (!teamId) return;
    api.get(`/athletes?team_id=${teamId}`).then(r => {
      setAthletes(r.data);
      setSelectedAthletes(r.data.map(a => a.id));
    }).catch(() => {});
  }, [api, teamId]);

  const toggleAthlete = (id) => {
    setSelectedAthletes(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // ── Start session ──
  const startSession = async () => {
    if (!teamId || !testType || selectedAthletes.length === 0) {
      toast.error(t('Select team, test type, and at least one athlete', 'Seleziona squadra, tipo e almeno un atleta'));
      return;
    }
    setLoading(true);
    try {
      const r = await api.post('/jump/sessions', {
        team_id: teamId, date: testDate, test_type: testType,
        notes, athlete_ids: selectedAthletes,
      });
      setSessionId(r.data.id);
      // Init entries for all athletes
      const init = {};
      selectedAthletes.forEach(aid => { init[aid] = {
        height: '', contactTime: '',
        // iter_119
        meanHeight: '', meanContact: '', area: '', powerDensity: '',
        meanFlight: '', minContact: '', maxFlight: '',
      }; });
      setEntries(init);
      setStep('testing');
      toast.success(t('Session created', 'Sessione creata'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
    setLoading(false);
  };

  // ── Record trial ──
  const recordTrial = async () => {
    const results = [];
    const isDrift = testType === 'DRIFT_2D';
    const isStiff = testType === 'STIFFNESS';
    for (const [aid, data] of Object.entries(entries)) {
      if (isDrift) {
        // Require all 4 Drift fields; skip athlete if any missing
        const mh = parseFloat(data.meanHeight);
        const mc = parseFloat(data.meanContact);
        const ar = parseFloat(data.area);
        const pd = parseFloat(data.powerDensity);
        if ([mh, mc, ar, pd].some((v) => isNaN(v)) || mc <= 0) continue;
        results.push({
          athlete_id: aid,
          trial_number: 1,
          side: currentSide,
          mean_height_cm: mh,
          mean_contact_time_s: mc,
          area_cm2: ar,
          power_density_w_kg_dm2: pd,
        });
      } else if (isStiff) {
        const mc = parseFloat(data.meanContact);
        const mf = parseFloat(data.meanFlight);
        if (isNaN(mc) || mc <= 0 || isNaN(mf)) continue;
        const entry = {
          athlete_id: aid,
          trial_number: 1,
          mean_contact_time_s: mc,
          mean_flight_time_s: mf,
        };
        const minC = parseFloat(data.minContact);
        const maxF = parseFloat(data.maxFlight);
        if (!isNaN(minC)) entry.min_contact_time_s = minC;
        if (!isNaN(maxF)) entry.max_flight_time_s = maxF;
        results.push(entry);
      } else {
        const h = parseFloat(data.height);
        if (isNaN(h) || h <= 0) continue;

        const entry = {
          athlete_id: aid,
          trial_number: trialNum,
          jump_height_cm: h,
        };

        if (testType === 'DROP_JUMP') {
          const ct = parseFloat(data.contactTime);
          if (isNaN(ct) || ct <= 0) {
            toast.error(t('Contact time required for Drop Jump', 'Tempo di contatto richiesto'));
            return;
          }
          entry.contact_time_ms = ct;
        }
        if (testType === 'CMJ_RIGHT_LEFT') {
          entry.side = currentSide;
        }
        if (testType === 'LOADED_CMJ') {
          entry.load_kg = loadKg;
        }

        results.push(entry);
      }
    }

    if (results.length === 0) {
      toast.error(t('Enter at least one height value', 'Inserisci almeno un valore'));
      return;
    }

    setLoading(true);
    try {
      const r = await api.post(`/jump/sessions/${sessionId}/results/batch`, { results });
      setSavedResults(prev => [...prev, ...r.data.results]);

      // Build label for the toast
      let label = `${t('Trial', 'Prova')} ${trialNum}`;
      if (testType === 'CMJ_RIGHT_LEFT') label += ` (${currentSide})`;
      if (testType === 'LOADED_CMJ') label += ` (${loadKg}kg)`;
      toast.success(`${label}: ${r.data.added} ${t('recorded', 'registrati')}`);

      // Reset entries
      const reset = {};
      selectedAthletes.forEach(aid => { reset[aid] = {
        height: '', contactTime: '',
        meanHeight: '', meanContact: '', area: '', powerDensity: '',
        meanFlight: '', minContact: '', maxFlight: '',
      }; });
      setEntries(reset);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
    setLoading(false);
  };

  // ── Finalize ──
  const finalize = async () => {
    if (savedResults.length === 0) {
      toast.error(t('No results recorded', 'Nessun risultato registrato'));
      return;
    }
    setLoading(true);
    try {
      const r = await api.post(`/jump/sessions/${sessionId}/finalize`);
      setFinalResult(r.data);
      setStep('results');
      toast.success(t('Session finalized', 'Sessione finalizzata'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
    setLoading(false);
  };

  const activeAthletes = useMemo(() =>
    athletes.filter(a => selectedAthletes.includes(a.id)),
    [athletes, selectedAthletes]
  );

  // Trial summary for sidebar
  const trialSummary = useMemo(() => {
    const byAthlete = {};
    for (const r of savedResults) {
      if (!byAthlete[r.athlete_id]) byAthlete[r.athlete_id] = [];
      byAthlete[r.athlete_id].push(r);
    }
    return byAthlete;
  }, [savedResults]);

  // ═══════════════════════════════════════════════════════
  // STEP 1 — Setup
  // ═══════════════════════════════════════════════════════
  if (step === 'setup') {
    return (
      <div className="space-y-6 fade-in" data-testid="jump-setup">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/staff/jump')} className="text-zinc-400 hover:text-zinc-100">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
              <Zap className="w-6 h-6 text-cyan-400" />
              {t('Group Jump Test', 'Test di Salto di Gruppo')}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">{t('Step 1 — Setup', 'Fase 1 — Impostazione')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select value={teamId} onValueChange={setTeamId}>
            <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="setup-team">
              <SelectValue placeholder={t('Select Team', 'Seleziona Squadra')} />
            </SelectTrigger>
            <SelectContent>
              {teams.map(tm => <SelectItem key={tm.id} value={tm.id}>{tm.code} — {tm.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={testType} onValueChange={setTestType}>
            <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="setup-type">
              <SelectValue placeholder={t('Select Test Type', 'Seleziona Tipo')} />
            </SelectTrigger>
            <SelectContent>
              {TEST_TYPES.map(tt => <SelectItem key={tt.value} value={tt.value}>{tt.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={testDate} onChange={e => setTestDate(e.target.value)}
            className="bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="setup-date" />
        </div>
        <Input placeholder={t('Notes (optional)', 'Note (opzionale)')} value={notes} onChange={e => setNotes(e.target.value)}
          className="bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="setup-notes" />

        {/* Athlete Selection */}
        {athletes.length > 0 && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-cyan-400" />
                {t('Athletes', 'Atleti')} ({selectedAthletes.length}/{athletes.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {athletes.map(a => {
                  const selected = selectedAthletes.includes(a.id);
                  return (
                    <button key={a.id} onClick={() => toggleAthlete(a.id)}
                      className={`p-3 rounded-sm border text-left transition-all ${
                        selected
                          ? 'border-cyan-500/50 bg-cyan-500/10 text-zinc-100'
                          : 'border-zinc-800 bg-zinc-950/50 text-zinc-500'
                      }`} data-testid={`athlete-select-${a.id}`}>
                      <div className="text-sm font-medium">{a.first_name} {a.last_name}</div>
                      <div className="text-xs mt-0.5">{a.primary_position}</div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Button onClick={startSession} disabled={!teamId || !testType || selectedAthletes.length === 0 || loading}
          className="w-full bg-cyan-600 hover:bg-cyan-700 text-white h-12 text-base" data-testid="start-session-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <>{t('Start Testing Session', 'Inizia Sessione di Test')} ({selectedAthletes.length} {t('athletes', 'atleti')})</>
          )}
        </Button>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════
  // STEP 2 — Testing Table
  // ═══════════════════════════════════════════════════════
  if (step === 'testing') {
    return (
      <div className="space-y-5 fade-in" data-testid="jump-testing">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-zinc-100 flex items-center gap-2">
              <Zap className="w-5 h-5 text-cyan-400" />
              {TEST_LABELS[testType] || testType}
            </h1>
            <p className="text-sm text-zinc-500">{testDate} • {activeAthletes.length} {t('athletes', 'atleti')} • {savedResults.length} {t('results saved', 'risultati salvati')}</p>
          </div>
          <Button onClick={finalize} disabled={savedResults.length === 0 || loading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="finalize-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
            {t('Finalize & Save', 'Finalizza e Salva')}
          </Button>
        </div>

        {/* Trial Controls */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">{t('Trial', 'Prova')}:</span>
            <Button variant="outline" size="sm" onClick={() => setTrialNum(n => Math.max(1, n - 1))}
              className="border-zinc-700 h-8 w-8 p-0"><Minus className="w-3 h-3" /></Button>
            <span className="text-lg font-bold text-cyan-400 w-6 text-center" data-testid="trial-num">{trialNum}</span>
            <Button variant="outline" size="sm" onClick={() => setTrialNum(n => n + 1)}
              className="border-zinc-700 h-8 w-8 p-0"><Plus className="w-3 h-3" /></Button>
          </div>

          {(testType === 'CMJ_RIGHT_LEFT' || testType === 'DRIFT_2D') && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-400">{t('Side', 'Lato')}:</span>
              <Button variant={currentSide === 'RIGHT' ? 'default' : 'outline'} size="sm"
                onClick={() => setCurrentSide('RIGHT')}
                className={currentSide === 'RIGHT' ? 'bg-blue-600' : 'border-zinc-700'}
                data-testid="side-right">R</Button>
              <Button variant={currentSide === 'LEFT' ? 'default' : 'outline'} size="sm"
                onClick={() => setCurrentSide('LEFT')}
                className={currentSide === 'LEFT' ? 'bg-blue-600' : 'border-zinc-700'}
                data-testid="side-left">L</Button>
            </div>
          )}

          {testType === 'LOADED_CMJ' && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-400">{t('Load', 'Carico')} (kg):</span>
              <Input type="number" value={loadKg} onChange={e => setLoadKg(parseFloat(e.target.value) || 0)}
                className="w-20 bg-zinc-900 border-zinc-700 text-zinc-200 h-8" data-testid="load-kg" />
            </div>
          )}

          <Button onClick={recordTrial} disabled={loading}
            className="ml-auto bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="record-trial-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('Record Trial', 'Registra Prova')}
          </Button>
        </div>

        {/* Data Entry Table */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-0">
            {testType === 'DRIFT_2D' ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Athlete', 'Atleta')}</th>
                    <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Mean H (cm)', 'H media (cm)')}</th>
                    <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Mean CT (s)', 'CT media (s)')}</th>
                    <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Area (cm²)', 'Area (cm²)')}</th>
                    <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('PD (W/kg/dm²)', 'PD (W/kg/dm²)')}</th>
                    <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Saved', 'Salvati')}</th>
                  </tr>
                </thead>
                <tbody>
                  {activeAthletes.map(a => {
                    const entry = entries[a.id] || {};
                    const saved = trialSummary[a.id] || [];
                    return (
                      <tr key={a.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                        <td className="p-3">
                          <div className="text-sm font-medium text-zinc-200">{a.first_name} {a.last_name}</div>
                          <div className="text-xs text-zinc-500">{a.primary_position}</div>
                        </td>
                        {['meanHeight','meanContact','area','powerDensity'].map((field) => (
                          <td className="p-3" key={field}>
                            <Input type="number" step="0.01" placeholder="0"
                              value={entry[field] || ''}
                              onChange={e => setEntries(prev => ({ ...prev, [a.id]: { ...prev[a.id], [field]: e.target.value } }))}
                              className="w-24 mx-auto bg-zinc-950 border-zinc-700 text-zinc-100 text-center h-9"
                              data-testid={`drift-${field}-${a.id}`} />
                          </td>
                        ))}
                        <td className="p-3 text-center text-xs text-zinc-400">{saved.length}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : testType === 'STIFFNESS' ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Athlete', 'Atleta')}</th>
                    <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Mean CT (s)', 'CT media (s)')} *</th>
                    <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Mean FT (s)', 'FT media (s)')} *</th>
                    <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Min CT (s)', 'CT min (s)')}</th>
                    <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Max FT (s)', 'FT max (s)')}</th>
                    <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Saved', 'Salvati')}</th>
                  </tr>
                </thead>
                <tbody>
                  {activeAthletes.map(a => {
                    const entry = entries[a.id] || {};
                    const saved = trialSummary[a.id] || [];
                    return (
                      <tr key={a.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                        <td className="p-3">
                          <div className="text-sm font-medium text-zinc-200">{a.first_name} {a.last_name}</div>
                          <div className="text-xs text-zinc-500">{a.primary_position}</div>
                        </td>
                        {['meanContact','meanFlight','minContact','maxFlight'].map((field) => (
                          <td className="p-3" key={field}>
                            <Input type="number" step="0.01" placeholder="0"
                              value={entry[field] || ''}
                              onChange={e => setEntries(prev => ({ ...prev, [a.id]: { ...prev[a.id], [field]: e.target.value } }))}
                              className="w-24 mx-auto bg-zinc-950 border-zinc-700 text-zinc-100 text-center h-9"
                              data-testid={`stiff-${field}-${a.id}`} />
                          </td>
                        ))}
                        <td className="p-3 text-center text-xs text-zinc-400">{saved.length}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Athlete', 'Atleta')}</th>
                  <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Height (cm)', 'Altezza (cm)')}</th>
                  {testType === 'DROP_JUMP' && (
                    <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Contact (ms)', 'Contatto (ms)')}</th>
                  )}
                  <th className="text-center p-3 text-xs text-zinc-500 uppercase tracking-wider">{t('Saved', 'Salvati')}</th>
                </tr>
              </thead>
              <tbody>
                {activeAthletes.map(a => {
                  const entry = entries[a.id] || { height: '', contactTime: '' };
                  const saved = trialSummary[a.id] || [];
                  const bestHeight = saved.length > 0 ? Math.max(...saved.map(s => s.jump_height_cm || 0)) : null;
                  return (
                    <tr key={a.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                      <td className="p-3">
                        <div className="text-sm font-medium text-zinc-200">{a.first_name} {a.last_name}</div>
                        <div className="text-xs text-zinc-500">{a.primary_position}</div>
                      </td>
                      <td className="p-3">
                        <Input type="number" step="0.1" placeholder="0.0"
                          value={entry.height}
                          onChange={e => setEntries(prev => ({
                            ...prev, [a.id]: { ...prev[a.id], height: e.target.value }
                          }))}
                          className="w-24 mx-auto bg-zinc-950 border-zinc-700 text-zinc-100 text-center h-9"
                          data-testid={`height-${a.id}`} />
                      </td>
                      {testType === 'DROP_JUMP' && (
                        <td className="p-3">
                          <Input type="number" step="1" placeholder="0"
                            value={entry.contactTime}
                            onChange={e => setEntries(prev => ({
                              ...prev, [a.id]: { ...prev[a.id], contactTime: e.target.value }
                            }))}
                            className="w-24 mx-auto bg-zinc-950 border-zinc-700 text-zinc-100 text-center h-9"
                            data-testid={`contact-${a.id}`} />
                        </td>
                      )}
                      <td className="p-3 text-center">
                        <span className="text-xs text-zinc-400">
                          {saved.length} {t('trials', 'prove')}
                          {bestHeight != null && (
                            <span className="text-cyan-400 ml-1">{t('best', 'max')}: {bestHeight}cm</span>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════
  // STEP 3 — Results
  // ═══════════════════════════════════════════════════════
  if (step === 'results' && finalResult) {
    const s = finalResult.summary;
    return (
      <div className="space-y-6 fade-in" data-testid="jump-results">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
              <Zap className="w-6 h-6 text-cyan-400" />
              {t('Results', 'Risultati')} — {TEST_LABELS[testType]}
            </h1>
            <p className="text-sm text-zinc-500">{testDate} • {s.athletes_tested} {t('athletes', 'atleti')}</p>
          </div>
          <Button onClick={() => navigate('/staff/jump')} className="bg-zinc-700 hover:bg-zinc-600 text-white" data-testid="back-to-dashboard-btn">
            {t('Back to Dashboard', 'Torna alla Dashboard')}
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {s.team_avg_height != null && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-zinc-100">{s.team_avg_height} cm</div>
                <div className="text-xs text-zinc-500">{t('Team Average', 'Media Squadra')}</div>
              </CardContent>
            </Card>
          )}
          {s.best_height != null && (
            <Card className="bg-emerald-500/10 border-emerald-500/30">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <div className="text-2xl font-bold text-emerald-400">{s.best_height} cm</div>
                <div className="text-xs text-zinc-500">{t('Best', 'Migliore')}</div>
              </CardContent>
            </Card>
          )}
          {s.lowest_height != null && (
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-4 text-center">
                <TrendingDown className="w-5 h-5 text-red-400 mx-auto mb-1" />
                <div className="text-2xl font-bold text-red-400">{s.lowest_height} cm</div>
                <div className="text-xs text-zinc-500">{t('Lowest', 'Minore')}</div>
              </CardContent>
            </Card>
          )}
          {s.team_avg_rsi != null && (
            <Card className="bg-cyan-500/10 border-cyan-500/30">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-cyan-400">{s.team_avg_rsi}</div>
                <div className="text-xs text-zinc-500">{t('Avg RSI', 'RSI Media')}</div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Alerts */}
        {finalResult.alerts.length > 0 && (
          <Card className="bg-red-500/5 border-red-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-red-400 text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {t('Alerts', 'Allerte')} ({finalResult.alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {finalResult.alerts.map((a, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-red-500/5 rounded-sm text-sm" data-testid={`result-alert-${i}`}>
                  <span className="text-zinc-200">{a.athlete_name}</span>
                  <span className="text-red-400 text-xs">
                    {a.type === 'PERFORMANCE_DROP' && `${t('Drop', 'Calo')} ${a.drop_pct}%`}
                    {a.type === 'ASYMMETRY' && `${t('Asymmetry', 'Asimmetria')} ${a.asymmetry_pct}%`}
                    {a.type === 'RSI_LOW' && `RSI ${a.rsi}`}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Athlete Rankings */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base">{t('Athlete Results', 'Risultati Atleti')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.values(s.athlete_summaries || {})
              .sort((a, b) => (b.height_cm || b.rsi || 0) - (a.height_cm || a.rsi || 0))
              .map((as, i) => (
                <div key={as.athlete_id} className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-sm border border-zinc-800"
                  data-testid={`result-athlete-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                      i === 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-800 text-zinc-500'
                    }`}>{i + 1}</span>
                    <span className="text-sm font-medium text-zinc-200">{as.athlete_name || as.athlete_id.slice(0, 8)}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    {as.height_cm != null && <span className="text-zinc-100 font-semibold">{as.height_cm} cm</span>}
                    {as.rsi != null && <span className="text-cyan-400 font-semibold">RSI {as.rsi}</span>}
                    {as.right_cm != null && (
                      <div className="text-right">
                        <span className="text-zinc-300">R: {as.right_cm} cm  L: {as.left_cm} cm</span>
                        <span className={`ml-2 text-xs font-semibold ${as.asymmetry_pct > 10 ? 'text-red-400' : 'text-emerald-400'}`}>
                          {as.asymmetry_pct}% {t('asymmetry', 'asimmetria')}
                        </span>
                      </div>
                    )}
                    {as.loads && (
                      <div className="text-xs text-zinc-400">
                        {Object.entries(as.loads).map(([load, h]) => (
                          <span key={load} className="mr-2">{load}kg: <span className="text-zinc-200">{h}cm</span></span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
